/**
 * HTML Sanitization Module
 *
 * Provides utilities for sanitizing HTML content to prevent XSS attacks
 * while allowing reasonable styling and formatting options.
 *
 * Uses DOMPurify via isomorphic-dompurify for server-side and client-side support.
 *
 * @module sanitize-html
 */
import DOMPurify from 'isomorphic-dompurify';

/**
 * Configuration for DOMPurify sanitization.
 * Allows common HTML elements and safe attributes while blocking scripts and event handlers.
 */
const DOMPURIFY_CONFIG = {
  // Allow these tags
  ALLOWED_TAGS: [
    // Structure
    'div', 'span', 'section', 'article', 'header', 'footer', 'main', 'aside', 'nav',
    // Text
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr',
    // Formatting
    'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup',
    // Lists
    'ul', 'ol', 'li',
    // Links and media
    'a', 'img',
    // Tables
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
    // Forms (display only - no inputs for custom HTML)
    'blockquote', 'pre', 'code',
    // Figure
    'figure', 'figcaption',
    // Styling (for custom CSS in whitelabel homepages)
    'style',
  ],
  // Allow these attributes
  ALLOWED_ATTR: [
    // Global attributes
    'class', 'id', 'style', 'title', 'lang', 'dir',
    // Link attributes
    'href', 'target', 'rel',
    // Image attributes
    'src', 'alt', 'width', 'height', 'loading',
    // Table attributes
    'colspan', 'rowspan', 'scope',
    // Accessibility
    'role',
    // Shortcode placeholder attributes (explicit for safety)
    'data-shortcode-type', 'data-shortcode-id', 'data-shortcode-config',
  ],
  // Additional security options - these enable wildcard patterns
  ALLOW_DATA_ATTR: true,  // Allows all data-* attributes
  ALLOW_ARIA_ATTR: true,  // Allows all aria-* attributes
  // Return string (not document)
  RETURN_DOM: false,
  RETURN_DOM_FRAGMENT: false,
  // Keep safe URI schemes
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 *
 * This function removes:
 * - Script tags and JavaScript execution
 * - Event handlers (onclick, onerror, etc.)
 * - javascript: URLs
 * - data: URLs (except safe image formats)
 * - Dangerous CSS properties
 *
 * While preserving:
 * - Common HTML structure (div, span, section, etc.)
 * - Text formatting (h1-h6, p, strong, em, etc.)
 * - Lists (ul, ol, li)
 * - Links with safe URLs (http, https, mailto, tel)
 * - Images with safe URLs
 * - Inline styles for customization
 * - Classes and IDs for styling hooks
 * - Data attributes for custom functionality
 *
 * @param html - The raw HTML string to sanitize
 * @returns Sanitized HTML string safe for rendering
 *
 * @example
 * ```typescript
 * import { sanitizeHtml } from '@/lib/sanitize-html';
 *
 * const userHtml = '<div onclick="alert(\'xss\')">Hello <script>bad()</script></div>';
 * const safeHtml = sanitizeHtml(userHtml);
 * // Returns: '<div>Hello </div>'
 * ```
 */
export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(html, DOMPURIFY_CONFIG) as string;
}

/**
 * Sanitizes HTML content with a more restrictive configuration for inline text.
 * Only allows basic text formatting, no structural elements or links.
 *
 * @param html - The raw HTML string to sanitize
 * @returns Sanitized HTML string with minimal formatting
 */
export function sanitizeInlineText(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  const strictConfig = {
    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup', 'br'],
    ALLOWED_ATTR: ['class'],
  };

  return DOMPurify.sanitize(html, strictConfig) as string;
}

/**
 * Checks if HTML content contains potentially dangerous elements before sanitization.
 * Useful for warning users about content that will be modified.
 *
 * @param html - The raw HTML string to check
 * @returns Object with flags for different types of unsafe content
 */
export function checkUnsafeContent(html: string): {
  hasScripts: boolean;
  hasEventHandlers: boolean;
  hasJavascriptUrls: boolean;
  hasUnsafeIframes: boolean;
} {
  if (!html || typeof html !== 'string') {
    return {
      hasScripts: false,
      hasEventHandlers: false,
      hasJavascriptUrls: false,
      hasUnsafeIframes: false,
    };
  }

  const lowerHtml = html.toLowerCase();

  return {
    hasScripts: /<script[\s>]/i.test(html),
    hasEventHandlers: /\bon\w+\s*=/i.test(html),
    hasJavascriptUrls: /javascript:/i.test(lowerHtml),
    hasUnsafeIframes: /<iframe/i.test(html),
  };
}

/**
 * Escapes a string for safe use in JavaScript contexts.
 * Prevents XSS when injecting user data into JavaScript strings.
 *
 * @param str - The string to escape
 * @returns Escaped string safe for use in JavaScript
 *
 * @example
 * ```typescript
 * const userInput = "Hello </script><script>alert('xss')";
 * const safeStr = escapeJsString(userInput);
 * // Can now safely use in: `const data = "${safeStr}";`
 * ```
 */
export function escapeJsString(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }

  return str
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * Strips all HTML tags and returns plain text.
 * Useful for extracting text content from HTML for previews or summaries.
 *
 * @param html - The HTML string to strip
 * @returns Plain text with all HTML removed
 */
export function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // First sanitize to remove any dangerous content
  const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] }) as string;

  // Decode HTML entities
  return sanitized
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
}
